/**
 * Acciones de transición de una orden (completar/entregar/anular/reactivar/
 * eliminar), con confirmación y manejo de error.
 *
 * Reusable desde la fila de la tabla y desde el modal de detalle. Decide qué
 * botones mostrar con `getAvailableActions` (estado + pago + permiso: el MISMO
 * que exige la API), por lo que una acción sin permiso no aparece
 * (`docs/frontend_architecture.md`, Pr. 5). Al pulsar, abre un diálogo de
 * confirmación (Radix `Dialog` de `components/ui`) y, al confirmar, ejecuta la
 * mutación `useOrderTransition` (que invalida los listados y el detalle). El
 * error de la API se traduce a un mensaje legible (`{error_code, message}`).
 *
 * Presentacional + orquestación de un hook de datos: no hace `fetch`; la red va
 * por la mutación.
 */
import { useState } from "react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

import { ACTION_CONFIRM, ACTION_LABEL, ORDER_ERROR_MESSAGE } from "../constants";
import { useOrderTransition } from "../hooks/useOrderTransition";
import type { OrderAction, OrderStatus, PaymentStatus } from "../types";
import { getAvailableActions } from "../useAvailableActions";

interface OrderActionsProps {
  orderId: number;
  orderNumber: string;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  /** Tamaño de los botones (compacto en la tabla, normal en el detalle). */
  size?: "sm" | "md";
}

/** Variante de botón por acción: destructivas en rojo, el resto secundario. */
const ACTION_VARIANT: Record<OrderAction, "secondary" | "danger" | "primary"> = {
  complete: "primary",
  deliver: "primary",
  cancel: "secondary",
  reactivate: "secondary",
  delete: "danger",
};

/** Traduce un error de transición a un mensaje legible para el usuario. */
function transitionErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return ORDER_ERROR_MESSAGE[error.error_code] ?? error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "No se pudo completar la acción.";
}

export function OrderActions({
  orderId,
  orderNumber,
  status,
  paymentStatus,
  size = "sm",
}: OrderActionsProps) {
  const { hasPermission } = useAuth();
  const transition = useOrderTransition();
  const [pending, setPending] = useState<OrderAction | null>(null);

  const actions = getAvailableActions({ status, paymentStatus, hasPermission });

  if (actions.length === 0) {
    return <span className="text-xs text-slate-400">Sin acciones</span>;
  }

  const errorText = transition.isError
    ? transitionErrorMessage(transition.error)
    : null;

  function closeDialog() {
    setPending(null);
    transition.reset();
  }

  function confirm() {
    if (pending === null) {
      return;
    }
    const action = pending;
    transition.mutate(
      { id: orderId, action },
      {
        onSuccess: () => {
          setPending(null);
        },
        // En error, el diálogo permanece abierto mostrando el mensaje legible.
      },
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {actions.map((action) => (
          <Button
            key={action}
            type="button"
            size={size}
            variant={ACTION_VARIANT[action]}
            onClick={() => {
              transition.reset();
              setPending(action);
            }}
          >
            {ACTION_LABEL[action]}
          </Button>
        ))}
      </div>

      <Dialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          }
        }}
      >
        <DialogContent aria-describedby={undefined}>
          <DialogTitle>
            {pending ? ACTION_LABEL[pending] : ""} orden {orderNumber}
          </DialogTitle>
          <DialogDescription>
            {pending ? ACTION_CONFIRM[pending] : ""}
          </DialogDescription>

          {errorText && (
            <p className="mt-3 text-sm text-red-600" role="alert">
              {errorText}
            </p>
          )}

          <div className="mt-5 flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={closeDialog}
              disabled={transition.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant={pending ? ACTION_VARIANT[pending] : "primary"}
              onClick={confirm}
              disabled={transition.isPending}
            >
              {transition.isPending ? "Procesando…" : "Confirmar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
